import appConfig from 'app.config'
import { userManager } from 'auth/services/auth.service'
import React, {
	Context,
	createContext,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
	IMenuDropdownItem,
	menuItems,
} from 'selfServicePortal/constants/menuItems'
import {
	ICreditBalance,
	IDepositData,
} from 'selfServicePortal/interfaces/i-api-creaditCard'
import {
	ICustomer,
	IMarketing,
} from 'selfServicePortal/interfaces/i-api-customer'
import getCreditCardDataService from '../services/CreditCardServices/getCreditCardDataService'
import getCustomerService from '../services/CustomerServices/getCustomerService'
import getDepositDataService from '../services/DepositServices/getDepositDataService'
import getAgreementHistoryService from '../services/CreditCardServices/getAgreementHistoryService'
import { AuthContext, getErrorWithFullMessage } from 'shared'
import {
	ISddMandate,
	ISddEligible,
} from 'selfServicePortal/interfaces/i-api-sdd'
import { appRoutes } from 'selfServicePortal/constants/routes'
import { countryValues } from 'selfServicePortal/constants/countryValues'
import {
	CountryValuesProductType,
	ICountryValues,
} from 'shared/constants/countryValueTypes'
import { ProductType } from 'selfServicePortal/interfaces/i-api-support'
import { useInformationModalService } from 'selfServicePortal/hooks/useInformationModalService'
import { IInformationModalProps } from 'shared/interfaces/i-api-informationModal'

interface ISspContext {
	isCard:
		| {
				isCard: boolean | undefined
				color?: string | undefined
				product?: string | undefined
		  }
		| undefined
	isDepositFixed:
		| {
				isCard: boolean | undefined
				isDepositFixed: boolean | undefined
				color?: string | undefined
				product?: string | undefined
		  }
		| undefined
	overviewData: ICreditBalance | IDepositData | undefined
	overviewDataList: (ICreditBalance | IDepositData)[] | undefined
	customerData: ICustomer | undefined
	component: IMenuDropdownItem | undefined
	loading: boolean
	success: any
	error: any
	isMarketing: IMarketing | undefined
	isSddEligible: ISddEligible | undefined
	sddRefreshData: boolean | undefined
	sddMandate: ISddMandate | undefined
	staticCountryValues: ICountryValues | undefined
	productType: ProductType | string
	hasCustomerSeenInformationModal: boolean
	informationModalData: IInformationModalProps[] | undefined
	hadleCloseInformationModal: Function
	setComponent: Function
	setSuccess: Function
	changeComponent: Function
	setOverviewData: Function
	typeOfAccount: Function
	setIsCard: Function
	setIsDepositFixed: Function
	setIsMarketing: Function
	setIsSddEligible: Function
	setSddMandate: Function
	setSddRefreshData: Function
}

const initialSspContext: ISspContext = {
	isCard: undefined,
	isDepositFixed: undefined,
	overviewData: undefined,
	overviewDataList: undefined,
	customerData: undefined,
	component: undefined,
	loading: false,
	success: {
		contactForm: false,
	},
	error: {},
	isMarketing: undefined,
	sddMandate: undefined,
	isSddEligible: undefined,
	sddRefreshData: undefined,
	staticCountryValues: undefined,
	productType: '',
	hasCustomerSeenInformationModal: false,
	informationModalData: [],
	hadleCloseInformationModal: () => {},
	setComponent: () => {},
	setSuccess: () => {},
	changeComponent: () => {},
	setOverviewData: () => {},
	typeOfAccount: () => {},
	setIsCard: () => {},
	setIsDepositFixed: () => {},
	setIsMarketing: () => {},
	setIsSddEligible: () => {},
	setSddMandate: () => {},
	setSddRefreshData: () => {},
}

export function deriveisCard(
	overviewData: ICreditBalance | IDepositData,
): overviewData is ICreditBalance {
	return 'creditLimit' in overviewData
}

const SspContext: Context<ISspContext> =
	createContext<ISspContext>(initialSspContext)

export function SspContextProvider({
	children,
}: PropsWithChildren<ReactNode>): ReactElement {
	const { isAuthenticated } = useContext(AuthContext)
	const { t } = useTranslation()
	const location = useLocation()

	const [component, setComponent] = useState<IMenuDropdownItem>(
		menuItems[0].items[0],
	)
	const [customerData, setCustomerData] = useState<ICustomer>()
	const [isCard, setIsCard] = useState<{ isCard: boolean; color?: string }>()
	const [
		hasCustomerSeenInformationModal,
		setHasCustomerSeenInformationModal,
	] = useState(false)
	const [isDepositFixed, setIsDepositFixed] = useState<{
		isCard: boolean
		isDepositFixed: boolean
		color?: string
		product?: string
	}>()
	const [overviewData, setOverviewData] = useState<
		ICreditBalance | IDepositData
	>()
	const [overviewDataList, setOverviewDataList] =
		useState<(ICreditBalance | IDepositData)[]>()
	const [success, setSuccess] = useState({
		setPassword: false,
		contactForm: false,
	})
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<Error>()
	const [isMarketing, setIsMarketing] = useState<IMarketing>()
	const [isSddEligible, setIsSddEligible] = useState<ISddEligible>({
		eligible: false,
		activeMandate: null,
	})
	const [sddMandate, setSddMandate] = useState<ISddMandate>()
	const [sddRefreshData, setSddRefreshData] = useState(false)
	const [staticCountryValues, setStaticCountryValues] =
		useState<ICountryValues>()
	const [productType, setProductType] = useState<ProductType | string>('')

	const { data: informationModalData } = useInformationModalService(t)

	const hadleCloseInformationModal = () =>
		setHasCustomerSeenInformationModal(true)

	const getCreditCardData = useCallback(
		async (userRef: string) => {
			try {
				const overview = await getCreditCardDataService(userRef, t)
				setOverviewData(overview as ICreditBalance)
			} catch (error: any) {
				setError(getErrorWithFullMessage(error))
			} finally {
				setLoading(false)
			}
		},
		[t],
	)

	const getDepositData = useCallback(
		async (userRef: string) => {
			try {
				setLoading(true)
				const depositData = await getDepositDataService(userRef, t)
				setOverviewData(depositData as IDepositData)
			} catch (error: any) {
				setError(getErrorWithFullMessage(error))
			} finally {
				setLoading(false)
			}
		},
		[t],
	)

	const getFullAgreementHistory = useCallback(async () => {
		try {
			const history = await getAgreementHistoryService(t)
			const all = [
				...history?.agreements?.creditCardAccounts!.sort((a, b) =>
					a.creditCardNo > b.creditCardNo ? -1 : 1,
				),
				...history?.agreements?.depositAccounts!,
			].filter((product) => {
				if (!product.productId.includes('VISA')) {
					if (product.hasOwnProperty('cardAgreementId')) {
						return (
							product as ICreditBalance
						).creditCardNo.startsWith('5')
					}
					return product
				}
				return undefined
			})

			setOverviewDataList(all)
			setCustomerData(history?.customer as ICustomer)
			setOverviewData(all[0])
		} catch (error: any) {
			setError(error)
		} finally {
			setLoading(false)
		}
	}, [t])

	const typeOfAccount = (productId: string) => {
		switch (productId) {
			case 'BBF.VISA_VSDC_CL.CRDACC_P1':
			case 'BBF.VISA_VSDC_CL.CRDACC_P2':
			case 'BBF.VISA_VSDC_CL.CRDACC_P3':
				return {
					isCard: true,
					isDepositFixed: false,
					color: 'blue',
					product: 'Credits Blue',
				}
			case 'BBF.VISA_VSDC_CG.CRDACC_P6':
			case 'BBF.VISA_VSDC_CG.CRDACC_P7':
				return {
					isCard: true,
					isDepositFixed: false,
					color: 'gold-no',
					product: 'Credits Gold',
				}
			case 'BBF.MC.CRDACC_P1':
				return {
					isCard: true,
					isDepositFixed: false,
					color: 'gold-mc-no',
					product: 'TF Bank Mastercard',
				}
			case 'tf_deposit.DEPOSIT.END':
				return {
					isCard: false,
					isDepositFixed: true,
					color: undefined,
					product: 'Festgeldkonto',
				}
			case 'BBF.DEPOSIT.END':
				return {
					isCard: false,
					isDepositFixed: false,
					color: undefined,
					product: 'Fastrentekonto',
				}
			case 'TF_DEU_DEPOSIT.DEPOSIT.END':
				return { isCard: false, color: undefined, product: 'Festgeld' }
			case 'BBF.DEPOSIT.OPEN':
			case 'tf_deposit.DEPOSIT.OPEN':
			default:
				return {
					isCard: false,
					isDepositFixed: false,
					color: undefined,
					product: 'Sparekonto',
				}
		}
	}

	const updateProductType = (isCard: boolean) => {
		setProductType(
			isCard
				? CountryValuesProductType.Card
				: overviewData?.productId === 'TF_DEU_DEPOSIT.DEPOSIT.END'
					? CountryValuesProductType.Deposit_Fixed
					: CountryValuesProductType.Deposit,
		)
	}

	useEffect(() => {
		if (isCard?.isCard !== undefined) {
			updateProductType(isCard.isCard)
		}
	}, [isCard?.isCard]) //eslint-disable-line

	useEffect(() => {
		if (overviewData) {
			setIsCard(typeOfAccount(overviewData.productId))
		}
	}, [overviewData])

	const getCustomer = useCallback(async () => {
		const userRef = await userManager
			.getUser()
			.then((user) => user?.profile.sub)
		try {
			setLoading(true)
			if (['de', 'at', 'it', 'es'].includes(appConfig.country)) {
				const customerData = await getCustomerService(t)
				setCustomerData(customerData as ICustomer)
				if (customerData.bankId === 'tf_deu_iss') {
					await getCreditCardData(userRef!)
					setIsCard({ isCard: true, color: 'standard-de' })
				} else if (customerData.bankId === 'TF_DEU_DEPOSIT') {
					await getDepositData(userRef!)
					setIsCard({ isCard: false })
				}
			} else {
				await getFullAgreementHistory()
			}
		} catch (error: any) {
			setError(getErrorWithFullMessage(error))
		} finally {
			setLoading(false)
		}
	}, [t, getFullAgreementHistory, getCreditCardData, getDepositData])

	useEffect(() => {
		const authCheck = async () => {
			if (isAuthenticated) {
				getCustomer()
			}
		}
		authCheck()
	}, [getCustomer, isAuthenticated])

	const GetStaticCountryValues = useCallback(() => {
		const value = countryValues.find(
			(item) => item.country === appConfig.country,
		)
		setStaticCountryValues(value)
	}, [])

	useEffect(() => {
		GetStaticCountryValues()
	}, [GetStaticCountryValues, isCard?.isCard])

	const changeComponent = useCallback((clickedItem: IMenuDropdownItem) => {
		menuItems.forEach((menuItem) => {
			menuItem.items.forEach((item) => {
				setComponent({ ...item, show: false })
			})
		})
		setComponent({ ...clickedItem, show: true })
	}, [])

	useEffect(() => {
		if (location.pathname === appRoutes.referfriends) {
			setComponent(menuItems[0].items[5])
		}
	}, [location.pathname])

	/*Not used currently, but leaving it here if it will be used ahead */
	//eslint-disable-next-line
	/* 	const getSddEligible = useCallback(
		async (partyId: string) => {
			setLoading(true)
			try {
				await sddService
					.sddCustomerEligible(partyId)
					.then((response) => {
						if (response.status === 200) {
							setIsSddEligible(response.data)
							if (
								location.pathname === appRoutes.sepadirectdebit
							) {
								navigate('/')
							}
						}
					})
					.then(() => {
						if (
							isSddEligible?.eligible &&
							isSddEligible.activeMandate &&
							!sddRefreshData
						) {
							getSddMandate(partyId)
						}
					})
					.catch((error) => {
						setError(error)
					})
					.finally(() => setLoading(false))
			} catch (error) {
				setLoading(false)
				throw error
			}
		},
		[isSddEligible, sddRefreshData, location, navigate],
	)
	const getSddMandate = async (partyId: string) => {
		await sddService
			.sddCustomerMandate(partyId)
			.then((response) => {
				if (response.status === 200) {
					let mandateData: ISddMandate = response.data
					setSddMandate(mandateData)
				}
			})
			.catch((error) => {
				throw error
			})
	} */

	return (
		<SspContext.Provider
			value={{
				isCard,
				overviewData,
				overviewDataList,
				customerData,
				component,
				loading,
				success,
				error,
				isMarketing,
				sddMandate,
				isSddEligible,
				sddRefreshData,
				staticCountryValues,
				productType,
				informationModalData,
				setComponent,
				setSuccess,
				changeComponent,
				setOverviewData,
				typeOfAccount,
				setIsCard,
				setIsMarketing,
				setIsSddEligible,
				setSddMandate,
				setSddRefreshData,
				isDepositFixed,
				setIsDepositFixed,
				hadleCloseInformationModal,
				hasCustomerSeenInformationModal,
			}}>
			{children}
		</SspContext.Provider>
	)
}

export default SspContext
